import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef, HostBinding,
  HostListener,
  inject,
  input,
  numberAttribute,
  signal,
} from '@angular/core';
import {
  distributeAndNormalize,
  getPathParamsForCorner,
  getSVGPathFromPathParams,
} from './squircle.service';

@Component({
  standalone: true,
  selector: 'cheelee-squircle',
  templateUrl: './squircle.component.html',
  styleUrl: './squircle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SquircleComponent implements AfterViewInit {
  @HostListener('window:resize')
  onWindowResize() {
    this.resize();
  }

  @HostBinding('style')
  get elStyle(): Record<string, string> {
    return {
      'clip-path': `path("${this.path()}")`,
    };
  }

  readonly radius = input.required({ transform: numberAttribute });

  private readonly elRef = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly elWidth = signal(1);

  protected readonly elHeight = signal(1);

  protected readonly path = computed(() => {
    const width = this.elWidth();
    const height = this.elHeight();
    const topLeftCornerRadius = this.radius();
    const topRightCornerRadius = this.radius();
    const bottomLeftCornerRadius = this.radius();
    const bottomRightCornerRadius = this.radius();
    const cornerSmoothing = 0.8;
    const preserveSmoothing = false;

    if (
      topLeftCornerRadius === topRightCornerRadius &&
      topRightCornerRadius === bottomRightCornerRadius &&
      bottomRightCornerRadius === bottomLeftCornerRadius &&
      bottomLeftCornerRadius === topLeftCornerRadius
    ) {
      const roundingAndSmoothingBudget = Math.min(width, height) / 2;
      const cornerRadius = Math.min(
        topLeftCornerRadius,
        roundingAndSmoothingBudget,
      );

      const pathParams = getPathParamsForCorner({
        cornerRadius,
        cornerSmoothing,
        preserveSmoothing,
        roundingAndSmoothingBudget,
      });

      return getSVGPathFromPathParams({
        width,
        height,
        topLeftPathParams: pathParams,
        topRightPathParams: pathParams,
        bottomLeftPathParams: pathParams,
        bottomRightPathParams: pathParams,
      });
    }

    const { topLeft, topRight, bottomLeft, bottomRight } = distributeAndNormalize(
      {
        topLeftCornerRadius,
        topRightCornerRadius,
        bottomRightCornerRadius,
        bottomLeftCornerRadius,
        width,
        height,
      },
    );

    return getSVGPathFromPathParams({
      width,
      height,
      topLeftPathParams: getPathParamsForCorner({
        cornerSmoothing,
        preserveSmoothing,
        cornerRadius: topLeft.radius,
        roundingAndSmoothingBudget: topLeft.roundingAndSmoothingBudget,
      }),
      topRightPathParams: getPathParamsForCorner({
        cornerSmoothing,
        preserveSmoothing,
        cornerRadius: topRight.radius,
        roundingAndSmoothingBudget: topRight.roundingAndSmoothingBudget,
      }),
      bottomRightPathParams: getPathParamsForCorner({
        cornerSmoothing,
        preserveSmoothing,
        cornerRadius: bottomRight.radius,
        roundingAndSmoothingBudget: bottomRight.roundingAndSmoothingBudget,
      }),
      bottomLeftPathParams: getPathParamsForCorner({
        cornerSmoothing,
        preserveSmoothing,
        cornerRadius: bottomLeft.radius,
        roundingAndSmoothingBudget: bottomLeft.roundingAndSmoothingBudget,
      }),
    });
  });

  ngAfterViewInit(): void {
    // TODO: Fix resize handling
    // Temporary fix
    setTimeout(() => this.resize(), 100);
  }

  private resize(): void {
    const nativeEl = this.elRef.nativeElement;

    this.elWidth.set(nativeEl.offsetWidth);
    this.elHeight.set(nativeEl.offsetHeight);
  }
}
